import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { RootState } from '../../../../reducers';
import { useUserTags } from '../../../../hooks/useTags';
import { FileType as GlobalFileType } from '../../../../types';
import {
  getGroqSessions,
  deleteMultiFileSession,
} from '../../../../services/multiFileAskAIService';
import { fetchFilesByIds } from '../constants';
import type { SavedConversation, ConversationMessage, Stage } from '../types';

interface UseSessionManagementReturn {
  // State
  savedConversations: SavedConversation[];
  isLoadingSessions: boolean;
  currentSessionId: string | null;
  conversationHistory: ConversationMessage[];
  selectedFiles: string[];
  cachedFiles: GlobalFileType[];
  autoSelectedFiles: GlobalFileType[];
  completion: string;
  stage: Stage;
  isLoadingFromHistory: boolean;

  // Setters (exposed for other hooks)
  setCurrentSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  setConversationHistory: React.Dispatch<
    React.SetStateAction<ConversationMessage[]>
  >;
  setSelectedFiles: React.Dispatch<React.SetStateAction<string[]>>;
  setCachedFiles: React.Dispatch<React.SetStateAction<GlobalFileType[]>>;
  setAutoSelectedFiles: React.Dispatch<React.SetStateAction<GlobalFileType[]>>;
  setCompletion: React.Dispatch<React.SetStateAction<string>>;
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
  setIsLoadingFromHistory: React.Dispatch<React.SetStateAction<boolean>>;

  // Actions
  clearChat: () => void;
  loadConversation: (conversationId: string) => void;
  deleteConversation: (
    conversationId: string,
    e?: React.MouseEvent,
  ) => Promise<void>;
  refreshSessions: () => Promise<void>;
  saveSessionId: (sessionId: string) => void;

  // Refs
  inputRef: React.RefObject<HTMLTextAreaElement>;
  justReceivedSessionIdRef: React.MutableRefObject<boolean>;
  hasLoadedInitialSessionRef: React.MutableRefObject<boolean>;
}

export function useSessionManagement(): UseSessionManagementReturn {
  const team = useSelector((state: RootState) => state.team.selectedTeam);
  const { getTag, setTag } = useUserTags();

  // State
  const [savedConversations, setSavedConversations] = useState<
    SavedConversation[]
  >([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(
    () => getTag('multi-ask-ai-session-id') || null,
  );
  const [conversationHistory, setConversationHistory] = useState<
    ConversationMessage[]
  >([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [cachedFiles, setCachedFiles] = useState<GlobalFileType[]>([]);
  const [autoSelectedFiles, setAutoSelectedFiles] = useState<GlobalFileType[]>(
    [],
  );
  const [completion, setCompletion] = useState<string>('');
  const [stage, setStage] = useState<Stage>('initial');
  const [isLoadingFromHistory, setIsLoadingFromHistory] = useState(false);

  // Refs
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const justReceivedSessionIdRef = useRef(false);
  const hasLoadedInitialSessionRef = useRef(false);

  // Helper to restore a session's state
  const restoreSession = useCallback(
    (conversationToLoad: SavedConversation) => {
      setIsLoadingFromHistory(true);
      setConversationHistory(conversationToLoad.conversation);

      const assistantMessage = conversationToLoad.conversation
        .slice()
        .reverse()
        .find((msg) => msg.role === 'assistant');

      if (assistantMessage) {
        setCompletion(assistantMessage.content);
      }

      const knownFiles = conversationToLoad.knownFiles || [];
      const allFileIds =
        knownFiles.length > 0
          ? knownFiles.map((f) => f.fileId)
          : conversationToLoad.fileIds;

      const autoSelectedFileIds = knownFiles
        .filter((f) => f.source !== 'direct')
        .map((f) => f.fileId);

      setSelectedFiles(allFileIds);
      setStage('final');

      // Fetch file data
      if (allFileIds.length > 0) {
        fetchFilesByIds(allFileIds).then((validFiles) =>
          setCachedFiles(validFiles),
        );
      } else {
        setCachedFiles([]);
      }

      // Fetch auto-selected files data
      if (autoSelectedFileIds.length > 0) {
        fetchFilesByIds(autoSelectedFileIds)
          .then((validFiles) => setAutoSelectedFiles(validFiles))
          .catch(() => setAutoSelectedFiles([]));
      } else {
        setAutoSelectedFiles([]);
      }
    },
    [],
  );

  // Clear chat
  const clearChat = useCallback(() => {
    hasLoadedInitialSessionRef.current = true;
    justReceivedSessionIdRef.current = false;

    setCompletion('');
    setStage('initial');
    setSelectedFiles([]);
    setCachedFiles([]);
    setAutoSelectedFiles([]);
    setCurrentSessionId(null);
    setConversationHistory([]);
    setTag({ key: 'multi-ask-ai-session-id', value: '' });
    setTag({ key: 'multi-ask-ai-cleared', value: 'true' });

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [setTag]);

  // Refresh sessions list from backend
  const refreshSessions = useCallback(async () => {
    if (!team?.teamId) return;
    try {
      const response = await getGroqSessions(1, 50);
      if (response.success && response.data) {
        setSavedConversations(response.data.sessions);
      }
    } catch (error) {
      console.error('Failed to refresh conversation history:', error);
    }
  }, [team?.teamId]);

  // Save session ID after a successful AI response
  const saveSessionId = useCallback(
    (sessionId: string) => {
      justReceivedSessionIdRef.current = true;
      setCurrentSessionId(sessionId);
      setTag({ key: 'multi-ask-ai-session-id', value: sessionId });
      setTag({ key: 'multi-ask-ai-cleared', value: '' });

      // Reset the flag after a delay
      setTimeout(() => {
        justReceivedSessionIdRef.current = false;
      }, 3000);
    },
    [setTag],
  );

  // Load a conversation from history
  const loadConversation = useCallback(
    (conversationId: string) => {
      const conversationToLoad = savedConversations.find(
        (conv) => conv._id === conversationId,
      );
      if (!conversationToLoad) return;

      hasLoadedInitialSessionRef.current = true;
      restoreSession(conversationToLoad);

      setCurrentSessionId(conversationId);
      setTag({ key: 'multi-ask-ai-session-id', value: conversationId });
    },
    [savedConversations, setTag, restoreSession],
  );

  // Delete a conversation
  const deleteConversation = useCallback(
    async (conversationId: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      try {
        await deleteMultiFileSession(conversationId);
        setSavedConversations((prev) =>
          prev.filter((conv) => conv._id !== conversationId),
        );
        toast('Session deleted successfully');
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        toast.error('Failed to delete session');
      }
    },
    [],
  );

  // Fetch sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      if (!team?.teamId) return;
      setIsLoadingSessions(true);
      try {
        const response = await getGroqSessions(1, 50);
        if (response.success && response.data) {
          setSavedConversations(response.data.sessions);
        }
      } catch (error) {
        console.error('Failed to fetch conversation history:', error);
        toast.error('Failed to load conversation history');
      } finally {
        setIsLoadingSessions(false);
      }
    };
    fetchSessions();
  }, [team?.teamId]);

  // Load previous session on mount if it exists
  useEffect(() => {
    if (savedConversations.length === 0) return;

    const sessionIdFromTag =
      (getTag('multi-ask-ai-session-id') as string) || null;

    if (hasLoadedInitialSessionRef.current) {
      if (sessionIdFromTag && sessionIdFromTag !== currentSessionId) {
        const sessionExists = savedConversations.some(
          (conv) => conv._id === sessionIdFromTag,
        );
        if (!sessionExists) return;
      } else {
        return;
      }
    }

    let conversationToLoad: SavedConversation | undefined;

    if (sessionIdFromTag) {
      conversationToLoad = savedConversations.find(
        (conv) => conv._id === sessionIdFromTag,
      );
      if (sessionIdFromTag !== currentSessionId) {
        setCurrentSessionId(sessionIdFromTag);
      }
    } else {
      const wasCleared = getTag('multi-ask-ai-cleared') === 'true';
      if (!wasCleared) {
        [conversationToLoad] = savedConversations;
      }
    }

    if (!conversationToLoad) {
      if (justReceivedSessionIdRef.current) {
        hasLoadedInitialSessionRef.current = true;
        return;
      }
      setCurrentSessionId(null);
      setTag({ key: 'multi-ask-ai-session-id', value: '' });
      hasLoadedInitialSessionRef.current = true;
      return;
    }

    restoreSession(conversationToLoad);

    if (conversationToLoad._id !== currentSessionId) {
      setCurrentSessionId(conversationToLoad._id);
      setTag({
        key: 'multi-ask-ai-session-id',
        value: conversationToLoad._id,
      });
    }

    hasLoadedInitialSessionRef.current = true;
  }, [savedConversations, setTag, getTag, currentSessionId, restoreSession]);

  return {
    savedConversations,
    isLoadingSessions,
    currentSessionId,
    conversationHistory,
    selectedFiles,
    cachedFiles,
    autoSelectedFiles,
    completion,
    stage,
    isLoadingFromHistory,

    setCurrentSessionId,
    setConversationHistory,
    setSelectedFiles,
    setCachedFiles,
    setAutoSelectedFiles,
    setCompletion,
    setStage,
    setIsLoadingFromHistory,

    clearChat,
    loadConversation,
    deleteConversation,
    refreshSessions,
    saveSessionId,

    inputRef,
    justReceivedSessionIdRef,
    hasLoadedInitialSessionRef,
  };
}
